import { Button, Select, Spinner, TextField } from '@cimpress/react-components';
import type { CSSProperties } from 'react';
import { useState } from 'react';
import {
    CardBuilder,
    Content,
    Control,
    Page,
    PreviewRow,
    PreviewRows,
    PreviewSection,
    type Card,
} from '../../Components';
import { useGeneratePatternParams } from '../../Components/InspirationGenerate/useGeneratePatternParams';
import type { SelectorOption } from '../../Components/common';
import { postGeneratePatternRequest } from '../../api/generatePattern';
import { isUrl, useCancelableEffect } from '../../tools';
import { IMAGE_COUNT_OPTIONS } from '../../common/constants';
import { PATTERN_STYLE_OPTIONS } from './constants';

const LOADING_CONTAINER_STYLE: CSSProperties = {
    textAlign: 'center' as const,
    padding: '20px',
};

const LOADING_TEXT_STYLE: CSSProperties = {
    marginTop: '10px',
};

const PATTERN_CARD_CONTENT_STYLE: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
};

const PATTERN_IMAGE_STYLE: CSSProperties = {
    maxWidth: '100%',
    height: 'auto',
    cursor: 'pointer',
};

export const GeneratePatternPage = () => {
    const { prompt, style, setPrompt, setStyle } = useGeneratePatternParams();
    const [resultRow, setResultRow] = useState(PreviewRow());
    const [isLoading, setIsLoading] = useState(false);
    const [callGenerate, setCallGenerate] = useState(false);
    const [imageCount, setImageCount] = useState(1);

    const onStyleChange = (selected: SelectorOption | null | undefined) => {
        if (!selected) return;
        setStyle(selected.value);
    };

    useCancelableEffect(
        (signal) => {
            (async () => {
                if (!callGenerate || !prompt.trim()) return;

                setIsLoading(true);
                const count = Math.max(1, imageCount);

                const resultPromises = Array.from({ length: count }, () =>
                    postGeneratePatternRequest({
                        prompt: prompt.trim(),
                        style: style || undefined,
                        signal,
                    }),
                );
                const results = await Promise.allSettled(resultPromises);

                const imageResultCards: Card[] = [];
                for (const result of results) {
                    if (result.status === 'fulfilled') {
                        const imageUrl = isUrl(result.value.imageUrl) ? result.value.imageUrl : '';
                        const card: Card = CardBuilder.ofType('childComponent')
                            .withTitle('Pattern')
                            .withChildComponent(() => (
                                <div style={PATTERN_CARD_CONTENT_STYLE}>
                                    {imageUrl ? (
                                        <a
                                            // snyk:ignore:javascript/dom_xss
                                            href={imageUrl}
                                            target="_blank"
                                            rel="noopener noreferrer" // Security: the new tab cannot control this; the original URL is not sent
                                            style={{ display: 'block' }}
                                        >
                                            {/* snyk:ignore:javascript/dom_xss */}
                                            <img src={imageUrl} alt="Generated Pattern" style={PATTERN_IMAGE_STYLE} />
                                        </a>
                                    ) : (
                                        <p style={LOADING_TEXT_STYLE}>Invalid image URL from API.</p>
                                    )}
                                </div>
                            ))
                            .build();
                        imageResultCards.push(card);
                    } else {
                        const errorCard: Card = CardBuilder.ofType('documentUrl')
                            .withTitle('Result')
                            .withLoader(async () => {
                                throw result.reason;
                            })
                            .build();
                        imageResultCards.push(errorCard);
                    }
                }
                setResultRow(PreviewRow(...imageResultCards));
                setIsLoading(false);
                setCallGenerate(false);
            })();
        },
        [callGenerate, prompt, style, imageCount],
    );

    const onClickGenerate = () => {
        setCallGenerate(true);
    };

    const onImageCountChange = (selected: SelectorOption | null | undefined) => {
        if (!selected) {
            return;
        }
        setImageCount(parseInt(selected.value));
    };

    return (
        <Page>
            <Page.Title>Generate Pattern</Page.Title>

            <Page.Controls>
                <Control spacing="standard">
                    <Control.Title>Prompt</Control.Title>
                    <TextField
                        label="Prompt"
                        onChange={(e) => setPrompt(e.target.value)}
                        value={prompt}
                        required
                        placeholder="e.g. lavender flowers"
                    />
                </Control>
                <Control spacing="standard">
                    <Control.Title>Style</Control.Title>
                    <Select
                        onChange={onStyleChange}
                        value={PATTERN_STYLE_OPTIONS.find((opt) => opt.value === style)}
                        options={PATTERN_STYLE_OPTIONS}
                        label="Style"
                    />
                </Control>

                <Control spacing="standard">
                    <Control.Title>Image Count</Control.Title>
                    <Select
                        onChange={onImageCountChange}
                        value={IMAGE_COUNT_OPTIONS.find((opt) => opt.value === imageCount.toString())}
                        options={IMAGE_COUNT_OPTIONS}
                        label="Image Count"
                    />
                </Control>
                <Control spacing="standard">
                    <Button onClick={onClickGenerate}>Generate</Button>
                </Control>
            </Page.Controls>

            <Page.Content>
                {isLoading ? (
                    <Content.Card sticky size="large">
                        <Content.Section>
                            <div style={LOADING_CONTAINER_STYLE}>
                                <Spinner />
                                <p style={LOADING_TEXT_STYLE}>Generating pattern...</p>
                            </div>
                        </Content.Section>
                    </Content.Card>
                ) : (
                    <PreviewSection
                        introduction="Generate a pattern image from a text prompt. Enter a prompt and optionally choose a style, then click Generate."
                        showIntro={!resultRow.length}
                        rows={PreviewRows(resultRow)}
                    />
                )}
            </Page.Content>
        </Page>
    );
};

export default GeneratePatternPage;
